import { getChipColor, themeService } from '../../services/ThemeService';
import { tailwindClasses } from '../../themes/tailwind-classes';
import type { ColorValue, Size } from '../types';

// Re-export from core
export * from '@supply-mover-2-0/core';

// Create default color rule - returns 'default' type which evaluates to null (uses textPrimary)
export function createDefaultColorRule() {
	return {
		type: 'default' as const,
	};
}

export interface SizeStyleConfig {
	fontSize: string;
	padding: string;
	iconSize: number;
}

// Size mappings for badges, pills, and text. 'xxs' and 'xl' have no dedicated
// entry; callers fall back to md.
export const SIZE_MAP: Record<'xs' | 'sm' | 'md' | 'lg', SizeStyleConfig> &
	Partial<Record<Size, SizeStyleConfig>> = {
	xs: {
		fontSize: '0.625rem',
		padding: '0.0625rem 0.375rem',
		iconSize: 10,
	},
	sm: {
		fontSize: '0.75rem',
		padding: '0.125rem 0.5rem',
		iconSize: 12,
	},
	md: {
		fontSize: '0.875rem',
		padding: '0.25rem 0.75rem',
		iconSize: 14,
	},
	lg: {
		fontSize: '1rem',
		padding: '0.375rem 1rem',
		iconSize: 16,
	},
};

// Apply color rules to style
export function applyColorToStyle(
	style: any,
	backgroundColor?: string,
	textColor?: string,
	borderColor?: string,
): any {
	return {
		...style,
		...(backgroundColor && { backgroundColor }),
		...(textColor && { color: textColor }),
		...(borderColor && { borderColor }),
	};
}

/**
 * Evaluates a conditional color rule against a data object
 * @param ruleCondition The conditional color rule to evaluate
 * @param data The data object containing field values
 * @returns true if the condition matches, false otherwise
 */
function evaluateColorCondition(
	ruleCondition: any,
	data: Record<string, any>,
): boolean {
	const fieldName = ruleCondition.field?.fieldName;
	const fieldValue = data[fieldName];

	if (fieldValue == null) {
		return false;
	}

	const value = ruleCondition.value;
	const ignoreCase = ruleCondition.ignoreCase === true;

	// Helper to compare values with optional case insensitivity
	const compareValues = (a: unknown, b: unknown): boolean => {
		if (ignoreCase && typeof a === 'string' && typeof b === 'string') {
			return a.toLowerCase() === b.toLowerCase();
		}
		return a === b;
	};

	switch (ruleCondition.operator) {
		case 'equals':
			return compareValues(fieldValue, value);

		case 'not_equals':
			return !compareValues(fieldValue, value);

		case 'in':
			if (Array.isArray(value)) {
				return value.some((v) => compareValues(fieldValue, v));
			}

			return compareValues(fieldValue, value);

		case 'not_in':
			if (Array.isArray(value)) {
				return !value.some((v) => compareValues(fieldValue, v));
			}

			return !compareValues(fieldValue, value);

		default:
			return false;
	}
}

/**
 * Coerce a resolved cell value into a string lookup key. Naive String(value)
 * would turn null/undefined/NaN/objects into 'null'/'undefined'/'NaN'/
 * '[object Object]' and accidentally match literal map keys with those
 * spellings; returning null signals "no match possible".
 */
function coerceLookupKey(value: unknown): string | null {
	if (typeof value === 'string') {
		return value;
	}
	if (typeof value === 'number' && Number.isFinite(value)) {
		return String(value);
	}
	if (typeof value === 'boolean' || typeof value === 'bigint') {
		return String(value);
	}
	return null;
}

/**
 * Match a resolved cell value against a config entry's `value`. Strict
 * equality first, then string-coerced equality so numeric/boolean cell
 * values match their authored string spellings (mirrors valueMappingEngine).
 */
function matchesEntryValue(resolved: unknown, entryValue: unknown): boolean {
	if (resolved === entryValue) {
		return true;
	}
	const resolvedKey = coerceLookupKey(resolved);
	const entryKey = coerceLookupKey(entryValue);
	return resolvedKey != null && entryKey != null && resolvedKey === entryKey;
}

// A meaning authored by a status_map / status / value-mapping rule
// (e.g. `{ type: 'warning' }`) resolved to the shared badge treatment. These
// are the same class tokens the `Badge` component uses, so a status reads the
// same in a list row as it does anywhere else in the product, and both themes
// come from the token layer rather than from anything computed here.
//
// 'error' is an authored alias for danger. 'neutral', 'muted', 'secondary' and
// 'default' all land on the quiet grey badge — a neutral has no vivid hue to
// be outlined with, so it stays a filled chip by design.
/**
 * PD-16707 — the same meanings as text colours, for an icon. A list icon
 * authored as "warning" rendered the default grey because it can only use a
 * text colour and the meaning resolved to badge classes alone.
 */
const INTENT_TEXT_TOKENS: Record<string, keyof typeof tailwindClasses> = {
	success: 'textSuccess',
	warning: 'textWarning',
	danger: 'textDanger',
	error: 'textDanger',
	info: 'textInfo',
	primary: 'textAccent',
	neutral: 'textSecondary',
	muted: 'textSecondary',
	secondary: 'textSecondary',
	default: 'textSecondary',
};

const INTENT_BADGE_TOKENS: Record<string, keyof typeof tailwindClasses> = {
	success: 'badgeSuccess',
	warning: 'badgeWarning',
	danger: 'badgeDanger',
	error: 'badgeDanger',
	info: 'badgeInfo',
	primary: 'badgePrimary',
	neutral: 'badgeDefault',
	muted: 'badgeDefault',
	secondary: 'badgeDefault',
	default: 'badgeDefault',
};

/**
 * Resolve a ColorValue that names a MEANING or a CATEGORY into presentation
 * classes drawn from the token layer.
 *
 * An explicit `bg`/`text`/`border` always wins. That is deliberate: tenant
 * saved views hold literal hexes captured at save time and must keep
 * rendering exactly as they were saved. Authoring a hex in a shipped list
 * configuration is legacy — name the meaning instead.
 */
function resolveSemanticColor(
	color: ColorValue | null | undefined,
): ColorValue | null {
	if (color == null) {
		return null;
	}
	if (color.bg != null || color.text != null || color.border != null) {
		return color;
	}

	// A named theme role — a coloured WORD rather than a badge. Only the rule
	// level understood `themeColor` before, so a ColorValue carrying one (a
	// conditional rule's defaultColor, a value-mapping entry) resolved to
	// nothing and the text rendered its inherited colour.
	//
	// Now that the values layer is generated from the tokens, a role resolved
	// here IS the palette rather than the pre-palette literal it used to be.
	if (typeof color.themeColor === 'string' && color.themeColor !== '') {
		const themeValues: Record<string, string | undefined> =
			themeService.getColors().values;
		const resolved = themeValues[color.themeColor];

		if (resolved != null) {
			return color.themeColor.toLowerCase().startsWith('bg')
				? { bg: resolved, opacity: color.opacity }
				: { text: resolved, opacity: color.opacity };
		}

		return color;
	}

	// A categorical bucket — peers that only need to be told apart, with no
	// success/warning/danger reading. Cycles the qualitative chip palette.
	if (color.category != null && Number.isFinite(color.category)) {
		return {
			classNames: getChipColor(color.category),
			opacity: color.opacity,
		};
	}

	const intent = color.type;
	if (
		intent == null ||
		!Object.prototype.hasOwnProperty.call(INTENT_BADGE_TOKENS, intent)
	) {
		return color;
	}

	const tokenKey = INTENT_BADGE_TOKENS[intent];
	if (tokenKey == null) {
		return color;
	}
	const textTokenKey = INTENT_TEXT_TOKENS[intent];

	return {
		classNames: tailwindClasses[tokenKey],
		...(textTokenKey != null
			? { textClassName: tailwindClasses[textTokenKey] }
			: {}),
		opacity: color.opacity,
	};
}

/**
 * A colour rule as AUTHORED — in a shipped config, or in a saved view captured
 * years ago. The shape is deliberately loose because both sources are data
 * this module has to survive rather than a type it controls; every branch
 * below narrows before reading.
 */
type ColorRuleInput = any;
type ColorRuleData = Record<string, any>;

/**
 * Evaluate color rules with optional data for conditional rules.
 *
 * Meaning-to-colour resolution happens ONCE, here, rather than in each branch
 * below — several branches (conditional rules, value mappings) previously
 * returned an authored value straight through, so a configuration naming a
 * meaning got no colour at all on those paths.
 *
 * @param rule The color rule configuration
 * @param data Optional data object for evaluating conditional rules
 * @returns ColorValue or null
 */
export function evaluateColorRule(
	rule: ColorRuleInput,
	data?: ColorRuleData,
): ColorValue | null {
	return resolveColorValue(evaluateColorRuleValue(rule, data));
}

/**
 * Resolve any authored ColorValue — from a rule, a value mapping, or a saved
 * view — into something renderable. Safe to apply more than once.
 */
export function resolveColorValue(
	color: ColorValue | null | undefined,
): ColorValue | null {
	return resolveSemanticColor(color);
}

function evaluateColorRuleValue(
	rule: ColorRuleInput,
	data?: ColorRuleData,
): ColorValue | null {
	if (!rule) {
		return null;
	}

	// Handle dynamic color rules - color comes from a field value
	if (rule.type === 'dynamic' && rule.colorField && data) {
		const colorValue = getNestedFieldValue(data, rule.colorField);
		if (colorValue) {
			return {
				bg: undefined,
				text: colorValue,
			};
		}

		return null;
	}

	// Handle field-based color rules - background color comes from a field value
	// Used for badges/chips where the background color is stored in the data
	if (rule.type === 'field' && rule.fieldName && data) {
		const colorValue = getNestedFieldValue(data, rule.fieldName);
		if (colorValue) {
			return {
				bg: colorValue,
				text: '#ffffff',
			};
		}

		return null;
	}

	// Handle conditional color rules
	if (rule.type === 'conditional' && rule.rules && data != null) {
		for (const ruleCondition of rule.rules) {
			if (evaluateColorCondition(ruleCondition, data)) {
				return ruleCondition.color ?? null;
			}
		}

		// Return default color if no condition matches
		if (rule.defaultColor != null) {
			return rule.defaultColor;
		}

		return null;
	}

	// Handle static color rules. The whole authored value is returned rather
	// than just bg/text — picking those two fields out silently discarded a
	// named MEANING, so a static rule was the one place a meaning could not be
	// expressed.
	if (rule.type === 'static' && rule.staticColor) {
		return rule.staticColor;
	}

	// Handle theme color rules - references theme color keys
	if (rule.type === 'theme' && rule.themeColor) {
		const themeColors = themeService.getColors();
		const colorKey: string = rule.themeColor;
		// Config themeColor keys are free-form strings; unknown keys resolve
		// to undefined (no color applied).
		const themeValues: Record<string, string | undefined> = themeColors.values;

		// Check if it's a text color key
		if (colorKey.startsWith('text')) {
			return {
				bg: undefined,
				text: themeValues[colorKey],
			};
		}

		// Check if it's a background color key
		if (
			colorKey.startsWith('bg') ||
			colorKey === 'background' ||
			colorKey === 'surface'
		) {
			return {
				bg: themeValues[colorKey],
				text: undefined,
			};
		}

		// For other color keys, use as text color
		return {
			bg: undefined,
			text: themeValues[colorKey],
		};
	}

	// Handle range-mapping color rules — match numeric value to a range
	if (rule.type === 'range-mapping' && rule.rangeMapping && data != null) {
		// The value to check comes from the field's data source, passed via
		// displayHelpers which resolves it before calling evaluateColorRule.
		// We receive the raw data object, so we need the resolved numeric value.
		// displayHelpers passes `data` which is the full row — the caller should
		// also pass the resolved value. For now, check `data.__resolvedValue`
		// which displayHelpers sets, or fall back to checking all ranges.
		const numValue =
			typeof data['__resolvedValue'] === 'number'
				? data['__resolvedValue']
				: typeof data['__resolvedValue'] === 'string'
					? parseFloat(data['__resolvedValue'])
					: null;

		if (numValue != null && !isNaN(numValue)) {
			for (const range of rule.rangeMapping) {
				const min =
					typeof range.min === 'string' ? parseFloat(range.min) : range.min;
				const max =
					typeof range.max === 'string' ? parseFloat(range.max) : range.max;
				if (!isNaN(min) && !isNaN(max) && numValue >= min && numValue <= max) {
					return range.color ?? null;
				}
			}
		}

		return null;
	}

	// status_map: look up the cell's resolved value in a value→color map.
	// Authored as { type: 'status_map', statusMap: { active: { type: 'warning' } } }.
	if (rule.type === 'status_map' && rule.statusMap != null && data != null) {
		const key = coerceLookupKey(data['__resolvedValue']);
		if (
			key != null &&
			Object.prototype.hasOwnProperty.call(rule.statusMap, key)
		) {
			return resolveSemanticColor(rule.statusMap[key]);
		}
		return null;
	}

	// status: ordered entry list matched against the cell's resolved value.
	// Authored as { type: 'status', rules: [{ value: 'sent', color: { type: 'success' } }] }.
	if (rule.type === 'status' && Array.isArray(rule.rules) && data != null) {
		const resolved = data['__resolvedValue'];
		for (const entry of rule.rules) {
			if (matchesEntryValue(resolved, entry.value)) {
				return resolveSemanticColor(entry.color);
			}
		}
		if (rule.defaultColor != null) {
			return resolveSemanticColor(rule.defaultColor);
		}
		return null;
	}

	// value_mapping / value-mapping with entries on the rule itself. The bare
	// { type: 'value-mapping' } form (entries on the sibling
	// styleConfig.valueMapping) is resolved upstream by displayHelpers via the
	// mapping result's color, so returning null here is correct for it.
	if (rule.type === 'value-mapping' || rule.type === 'value_mapping') {
		if (Array.isArray(rule.valueMapping) && data != null) {
			const resolved = data['__resolvedValue'];
			for (const entry of rule.valueMapping) {
				if (matchesEntryValue(resolved, entry.value)) {
					return resolveSemanticColor(entry.color);
				}
			}
		}
		return null;
	}

	// boolean_map: color by the cell's boolean value ('true'/'false' string
	// spellings included, mirroring valueMappingEngine's boolean handling).
	if (rule.type === 'boolean_map' && rule.booleanMap != null && data != null) {
		const resolved = data['__resolvedValue'];
		const boolKey =
			typeof resolved === 'boolean'
				? resolved
				: resolved === 'true'
					? true
					: resolved === 'false'
						? false
						: null;
		if (boolKey != null) {
			return resolveSemanticColor(
				boolKey ? rule.booleanMap.true : rule.booleanMap.false,
			);
		}
		return null;
	}

	// Handle default type
	if (rule.type === 'default') {
		return null; // Use default theme colors
	}

	// Fallback for old format
	return {
		bg: rule?.backgroundColor,
		text: rule?.textColor,
	};
}

// Helper to get nested field value
function getNestedFieldValue(data: any, fieldPath: string): any {
	if (!data || !fieldPath) {
		return null;
	}

	const keys = fieldPath.split('.');
	let value = data;

	for (const key of keys) {
		if (value == null) {
			return null;
		}
		value = value[key];
	}

	return value;
}
